import { recipe } from '@vanilla-extract/recipes';
import { globalStyle, style, styleVariants, type StyleRule } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { iconTokens } from '../../tokens/categories/icon.tokens';
import { mobile } from '../../theme/media';

/**
 * 한 변 px. 로컬 아이콘 버튼 실측(2026-10-02, 131건 중 치수 판독분)이 18~24px 34건 · 28px 6건 ·
 * 32~36px 7건으로 갈려 Button 의 size 이름에 정사각 값을 붙였다.
 */
const SQUARE = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
} as const;

const TOUCH_TARGET = 44;

const sizeVariant = (px: number): StyleRule => {
  const bleed = (TOUCH_TARGET - px) / 2;
  if (bleed <= 0) return { width: `${px}px`, height: `${px}px` };
  return {
    width: `${px}px`,
    height: `${px}px`,
    '@media': {
      // 보이는 박스는 그대로 두고 탭 영역만 44px 로 넓힌다 (DESIGN.md §5).
      [mobile]: {
        '::after': {
          content: '""',
          position: 'absolute',
          inset: `-${bleed}px`,
        },
      },
    },
  };
};

/**
 * 표면은 Button 의 ghost 를 물려받지 않는다. 로컬 아이콘 버튼 120개의 hover 실측이
 * 색 변화 91 · 옅은 배경 45 · 테두리 18 이고, ghost 의 primary 테두리를 32px 아이콘에 씌우면
 * 상자가 무거워진다. 평소엔 투명·무테, hover 에서 글리프가 밝아지고 옅은 면이 깔린다.
 */
export const iconButtonRecipe = recipe({
  base: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
    border: 'none',
    borderRadius: vars.spacing.borderRadius.md,
    background: 'transparent',
    color: vars.typography.textColor.tertiary,
    cursor: 'pointer',
    transition: `background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
    selectors: {
      '&:hover:not(:disabled)': {
        background: vars.surface.highlight.light,
        color: vars.typography.textColor.primary,
      },
      '&:focus-visible': {
        outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
        outlineOffset: vars.focus.ring.offset,
      },
      '&:disabled': {
        opacity: 0.6,
        color: vars.typography.textColor.disabled,
        cursor: 'not-allowed',
      },
    },
  },
  variants: {
    size: {
      xs: sizeVariant(SQUARE.xs),
      sm: sizeVariant(SQUARE.sm),
      md: sizeVariant(SQUARE.md),
      lg: sizeVariant(SQUARE.lg),
    },
    tone: {
      default: {},
      danger: {
        selectors: {
          '&:hover:not(:disabled)': {
            background: vars.bright.status.errorBg,
            color: vars.bright.status.error,
          },
        },
      },
    },
  },
  defaultVariants: {
    size: 'sm',
    tone: 'default',
  },
});

export const iconSlot = style({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * 버튼 크기가 글리프 크기를 정한다. 호출부가 고른 size 속성(예: Phosphor `size={20}`)은 무시된다 —
 * 같은 sm 버튼 안의 X 가 호출부마다 제각각 커지지 않게. sm 14px 은 로컬 `×`(20px 글꼴)의 잉크 폭과 비슷하다.
 */
const GLYPH = {
  xs: iconTokens.size.xs,
  sm: iconTokens.size.sm,
  md: iconTokens.size.md,
  lg: iconTokens.size.xl,
} as const;

export const glyphSize = styleVariants(GLYPH, () => ({}));

for (const [size, px] of Object.entries(GLYPH) as [keyof typeof GLYPH, number][]) {
  globalStyle(`${glyphSize[size]} svg`, { width: `${px}px`, height: `${px}px` });
}
